import { useState, useCallback } from 'react';
import { summarizeAttendance, summarizeFinance, summarizeSchedule, summarizeVisitLogs } from '../../utils';

const AI_ANALYSIS_TYPES = [
  {
    id: 'briefing',
    label: '주간 브리핑',
    prompt: '이번 주 사역 전체 현황을 종합 브리핑해줘. 출석, 재정, 심방 상태를 간결하게 요약하고 즉시 실행할 사항을 3가지 제안해줘.'
  },
  {
    id: 'attendance',
    label: '출석 분석',
    prompt: '출석 데이터를 상세히 분석해줘. 장기 결석자 명단, 출석률 추이, 특이사항을 포함해줘. 심방이 시급한 사람이 있다면 우선순위를 매겨줘.'
  },
  {
    id: 'finance',
    label: '재정 분석',
    prompt: '재정 현황을 상세히 분석해줘. 총 잔액, 이번 달 수입/지출 내역, 예산 대비 사용률을 포함해줘.'
  },
  {
    id: 'pastoral',
    label: '목양 대상자',
    prompt: '심방과 목양이 필요한 사람들을 우선순위별로 정리해줘. 장기 결석자, 초신자, 관심필요 상태인 분들을 포함해줘.'
  },
  {
    id: 'birthday',
    label: '생일/기념일',
    prompt: '이번 달 생일인 학생과 교사를 모두 알려줘. 날짜순으로 정리하고, 축하 메시지 가이드도 간단히 제안해줘.'
  },
  {
    id: 'teacher',
    label: '교사 현황',
    prompt: '교사들의 사역 현황을 정리해줘. 각 교사의 담당 학생 수, 최근 심방 활동, 그리고 추가 지원이 필요한 교사가 있는지 분석해줘.'
  }
];

export { AI_ANALYSIS_TYPES };

export default function useStatisticsAiBriefing({
  selectedDepartment,
  students = [],
  teachers = [],
  attendanceLogs = [],
  finances = [],
  visitLogs = [],
  schedules = []
}) {
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [activeAnalysisType, setActiveAnalysisType] = useState('briefing');

  const closeAiModal = useCallback(() => {
    setIsAiModalOpen(false);
  }, []);

  const handleAiBriefing = useCallback(async (forceRefresh = false, analysisType = null) => {
    const type = analysisType || activeAnalysisType;
    setActiveAnalysisType(type);
    setIsAiModalOpen(true);

    // 1. Check Cache (by type)
    const today = new Date().toDateString();
    const cacheKey = `aaron_stats_${type}_${today}_${selectedDepartment || 'ALL'}`;
    const cachedResult = localStorage.getItem(cacheKey);

    if (cachedResult && !forceRefresh) {
      setAiResult(cachedResult);
      return;
    }

    // 2. Build context from real data
    setIsAiLoading(true);
    setAiResult("");

    const analysisConfig = AI_ANALYSIS_TYPES.find(t => t.id === type) || AI_ANALYSIS_TYPES[0];

    try {
      const summaryContext = {
        attendance: summarizeAttendance(students, attendanceLogs),
        finance: summarizeFinance(finances),
        schedule: summarizeSchedule(schedules),
        visit: summarizeVisitLogs(students, visitLogs)
      };

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'generate',
          message: analysisConfig.prompt,
          history: [],
          context: summaryContext,
          students: students.map(s => ({
            name: s.name,
            grade: s.grade,
            department: s.department,
            faithState: s.faithState,
            birthday: s.birthday,
            attendanceStrk: s.attendanceStrk
          })),
          teachers: teachers.map(t => ({
            name: t.name,
            class: t.class,
            birthday: t.birthday
          })),
          finances: finances.slice(-30) // Last 30 finance records
        })
      });

      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }

      const data = await res.json();
      const reply = data.reply || "분석 결과를 가져올 수 없습니다.";

      setAiResult(reply);

      // 3. Save to Cache
      localStorage.setItem(cacheKey, reply);
    } catch (e) {
      console.error("AI Briefing Error:", e);
      setAiResult(`AI 분석 중 오류가 발생했습니다.\n\n오류 내용: ${e.message}\n\n서버가 실행 중인지 확인해주세요.`);
    } finally {
      setIsAiLoading(false);
    }
  }, [selectedDepartment, students, teachers, attendanceLogs, finances, visitLogs, schedules, activeAnalysisType]);

  return {
    aiResult,
    closeAiModal,
    handleAiBriefing,
    isAiLoading,
    isAiModalOpen,
    activeAnalysisType,
    setActiveAnalysisType,
    analysisTypes: AI_ANALYSIS_TYPES,
  };
}
